import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { books, apiErrorMessage, type UploadResult } from '../lib/api';
import { useTranslation } from '../i18n';
import { Sidenote, SidenoteLayout } from '../components/ui';

export default function UploadPage() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [country, setCountry] = useState('KR');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setError(t('upload.chooseFirst'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await books.upload(file, country);
      setResult(r);
      if (r.dedupe_status === 'dedupe_hit' && r.book_id) {
        nav(`/books/${r.book_id}`);
      } else if (r.payment_url) {
        window.location.assign(r.payment_url);
      }
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="container upload-page">
      <SidenoteLayout>
        <SidenoteLayout.Main>
          <h1>{t('upload.title')}</h1>
          <p className="upload-help">{t('upload.help')}</p>
          <form onSubmit={onSubmit} className="upload-form">
            <label htmlFor="upload-pdf">
              {t('upload.fileLabel')}
              <input
                id="upload-pdf"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <label htmlFor="upload-country">
              {t('upload.countryLabel')}
              <select
                id="upload-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="KR">대한민국 (KRW)</option>
                <option value="US">United States (USD)</option>
                <option value="JP">日本 (JPY)</option>
              </select>
            </label>
            {error && <p className="upload-error" role="alert">{error}</p>}
            <button type="submit" disabled={submitting || !file} className="btn btn-accent">
              {submitting ? t('upload.submitting') : t('upload.submit')}
            </button>
          </form>

          {result && (
            <section className="upload-result" aria-live="polite">
              <h2>{t('upload.resultTitle')}</h2>
              <dl>
                <dt>{t('upload.dedupeStatus')}</dt><dd>{result.dedupe_status}</dd>
                <dt>{t('upload.paymentStatus')}</dt><dd>{result.payment_status}</dd>
                {result.payment_amount != null && (
                  <>
                    <dt>{t('upload.amount')}</dt>
                    <dd>{result.payment_amount} {result.payment_currency}</dd>
                  </>
                )}
                {result.job_id && <><dt>{t('upload.jobId')}</dt><dd>{result.job_id}</dd></>}
                {result.eta_minutes != null && (
                  <><dt>{t('upload.eta')}</dt><dd>~{result.eta_minutes} min</dd></>
                )}
              </dl>
              {result.job_id && (
                <button onClick={() => nav(`/jobs/${result.job_id}`)} className="btn btn-secondary">
                  {t('upload.watchProgress')}
                </button>
              )}
            </section>
          )}
        </SidenoteLayout.Main>

        <SidenoteLayout.Aside>
          <Sidenote label={t('upload.aside.formatLabel')} marker="·">
            <p>{t('upload.aside.formatBody')}</p>
          </Sidenote>
          <Sidenote label={t('upload.aside.etaLabel')} tone="accent">
            <p>{t('upload.aside.etaBody')}</p>
          </Sidenote>
          <Sidenote label={t('upload.aside.privacyLabel')} tone="mute">
            <p>{t('upload.aside.privacyBody')}</p>
          </Sidenote>
        </SidenoteLayout.Aside>
      </SidenoteLayout>
    </main>
  );
}
